import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import { BUG_REPORT_MANAGER_STATUSES, type BugReportStatus, type BugReportTriage } from '../../types';
import { BugStatusBadge, BugHistory, BugMedia } from './BugStatus';

/**
 * D-323 s6/s8 + D-341 (5), on a bug report's ticket page. For the OTE manager the panel is only three things:
 *   (a) "שליחה ל-Claude" - an optional note to Claude, then triage = claude (the note travels to the MCP with the report);
 *   (b) "אטפל בעצמי" - triage = self, and a thread with the reporter (they see it in "הדיווחים שלי" and get a notification);
 *   (c) the status: new / בטיפול / טופל / לא יטופל (the reporter is notified of a change).
 * The verdict / decision / note fields are gone from the screen (the API keeps them for the MCP).
 */
export default function BugTreatmentPanel({ ticketId }: { ticketId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // The server's rule for the triage / status / reply routes: the OTE manager (role admin; the permissions API's isAdmin / General Manager).
  // A D-335 "edit" override on "דיווחי באגים" only opens READS (elevationScreenForPath: GET/HEAD), so it does not show these buttons.
  const role = useAuthStore((s) => s.user?.role);
  const perms = usePermissions();
  const isAdmin = role === 'admin' || perms.isAdmin || perms.capabilities.isGeneralManager;
  const { data } = useQuery({
    queryKey: ['bug-report', ticketId],
    queryFn: () => api.getBugReport(ticketId),
    staleTime: 30_000,
  });
  const ctx = data?.data?.context;

  const [claudeOpen, setClaudeOpen] = useState(false);
  const [claudeNote, setClaudeNote] = useState('');
  const [reply, setReply] = useState('');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['bug-report', ticketId] });
    void qc.invalidateQueries({ queryKey: ['bug-reports'] });
  };

  const triage = useMutation({
    mutationFn: (v: { triage: BugReportTriage; note?: string }) => api.triageBugReport(ticketId, v.triage, v.note),
    onSuccess: (_r, v) => {
      toast('success', t(v.triage === 'claude' ? 'bugFlow.claudeSent' : 'bugFlow.triageSaved'));
      setClaudeOpen(false);
      setClaudeNote('');
      refresh();
    },
    onError: (e) => toast('error', apiErrorMessage(e, t('bugReport.submitError'))),
  });

  const setStatus = useMutation({
    mutationFn: (status: BugReportStatus) => api.reviewBugReport(ticketId, { report_status: status }),
    onSuccess: () => { toast('success', t('bugFlow.statusSaved')); refresh(); },
    onError: (e) => toast('error', apiErrorMessage(e, t('bugReport.submitError'))),
  });

  const sendReply = useMutation({
    mutationFn: () => api.replyBugReport(ticketId, reply.trim()),
    onSuccess: () => { toast('success', t('bugFlow.replySent')); setReply(''); refresh(); },
    onError: (e) => toast('error', apiErrorMessage(e, t('bugReport.submitError'))),
  });

  if (!ctx) return null;
  const currentTriage = ctx.triage || 'pending';
  const current = ctx.report_status || 'new';
  // A status set by the review / fix flow (e.g. "תוקן במקומי") stays visible in the selector even though it is not one of the four.
  const statusOptions: BugReportStatus[] = BUG_REPORT_MANAGER_STATUSES.includes(current) ? BUG_REPORT_MANAGER_STATUSES : [current, ...BUG_REPORT_MANAGER_STATUSES];

  return (
    <div className="card space-y-3" data-testid="bug-treatment">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-1">{t('bugFlow.treatTitle')}</h3>
        <BugStatusBadge status={current} verdict={ctx.verdict} />
      </div>
      {(ctx.page_title || ctx.page_url) && (
        <p className="text-xs text-text-3">
          {t('bugFlow.page')}: {ctx.page_title || ''} <span dir="ltr" className="break-all">{ctx.page_url || ''}</span>
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {ctx.screenshot_path && <BugMedia ticketId={ticketId} kind="screenshot" label={t('bugFlow.markedArea')} />}
        {ctx.full_screenshot_path && <BugMedia ticketId={ticketId} kind="full" label={t('bugFlow.fullScreenshot')} />}
        {ctx.audio_path && <BugMedia ticketId={ticketId} kind="audio" label={t('bugFlow.voiceNote')} />}
      </div>

      {isAdmin && (
        <div className="space-y-3 rounded-lg border border-border p-3" data-testid="bug-triage">
          {/* (c) status */}
          <label className="flex flex-wrap items-center gap-2 text-sm text-text-2">
            {t('bugFlow.treatStatus')}:
            <select
              className="input w-auto min-w-40"
              value={current}
              disabled={setStatus.isPending}
              onChange={(e) => setStatus.mutate(e.target.value as BugReportStatus)}
              data-testid="bug-status-select"
            >
              {statusOptions.map((s) => <option key={s} value={s}>{t(`bugFlow.status.${s}`)}</option>)}
            </select>
          </label>

          {/* (a) and (b) */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn btn-primary"
              disabled={triage.isPending || currentTriage === 'claude'}
              onClick={() => setClaudeOpen((o) => !o)}
              data-testid="bug-to-claude"
            >
              {currentTriage === 'claude' ? t('bugFlow.claudeSent') : t('bugFlow.sendToClaude')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={triage.isPending || currentTriage === 'self'}
              onClick={() => triage.mutate({ triage: 'self' })}
              data-testid="bug-handle-myself"
            >
              {t('bugFlow.handleMyselfBtn')}
            </button>
          </div>

          {claudeOpen && currentTriage !== 'claude' && (
            <form
              className="space-y-2"
              onSubmit={(e) => { e.preventDefault(); triage.mutate({ triage: 'claude', note: claudeNote.trim() || undefined }); }}
            >
              <label className="block text-xs text-text-2">
                {t('bugFlow.claudeNoteLabel')}
                <textarea className="input mt-1 w-full min-h-20 resize-y" value={claudeNote} onChange={(e) => setClaudeNote(e.target.value)} placeholder={t('bugFlow.claudeNotePlaceholder')} maxLength={3000} data-testid="bug-claude-note" />
              </label>
              <button type="submit" className="btn btn-primary btn-sm" disabled={triage.isPending} data-testid="bug-claude-send">
                {t('bugFlow.claudeSend')}
              </button>
            </form>
          )}
          {currentTriage === 'claude' && ctx.claude_note && (
            <p className="whitespace-pre-wrap rounded-lg bg-surface-subtle p-2 text-sm text-text-2" data-testid="bug-claude-note-shown">
              <span className="block text-xs text-text-3">{t('bugFlow.claudeNoteShown')}</span>
              {ctx.claude_note}
            </p>
          )}

          {/* (b) the thread with the reporter */}
          {currentTriage === 'self' && (
            <div className="space-y-2" data-testid="bug-thread">
              <h4 className="text-sm font-medium text-text-1">{t('bugFlow.threadTitle')}</h4>
              <p className="text-xs text-text-3">{t('bugFlow.threadHint')}</p>
              <BugHistory items={ctx.status_history ?? []} />
              <form
                className="space-y-2"
                onSubmit={(e) => { e.preventDefault(); if (reply.trim()) sendReply.mutate(); }}
              >
                <textarea className="input w-full min-h-16 resize-y" value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t('bugFlow.replyPlaceholder')} maxLength={3000} data-testid="bug-reply-text" aria-label={t('bugFlow.replyLabel')} />
                <button type="submit" className="btn btn-primary btn-sm" disabled={sendReply.isPending || !reply.trim()} data-testid="bug-reply-send">
                  {t('bugFlow.replySend')}
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {(!isAdmin || currentTriage !== 'self') && <BugHistory items={ctx.status_history ?? []} />}
    </div>
  );
}
